import { Box, Typography } from "@mui/material";
import useAPI from "../../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../../api/hooks/useColors";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import SvgToIcon from "../../../../../../../../icons/SvgToIcon";
import { ActivityLogType } from "../../../../../../../../utils/constants";
import { icons } from "../../../../../../../stack/environment/icons/iconExport";
import { IntegrationsMapping } from "../../../../../../../stack/integrations/IntegrationsMapping";
import { CustomLogTypography, EntityLink } from "./utils";

const TicketLink = ({ log }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const maxNameSize = 20;

  const tooltipContent = (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography variant="caption">Open in a new tab</Typography>
      <SvgToIcon IconLogo={icons.NewTabIcon} width={16} />
    </Box>
  );
  const ticketName =
    log.ticket_name.length > maxNameSize
      ? log.ticket_name.slice(0, maxNameSize - 3) + "..."
      : log.ticket_name;

  const handleClick = () => {
    trackEvent(
      `User clicked on activity log link to external ticket page in control ${log.control_id}`
    );
    window.open(log.ticket_url, "_blank");
  };

  return (
    <CustomTooltip
      title={tooltipContent}
      placement="top"
      arrow
      disableInteractive
    >
      <Box display="inline-block">
        <CustomLogTypography
          onClick={handleClick}
          color={colors.primary_light}
          sx={{ cursor: "pointer" }}
        >
          {ticketName}
        </CustomLogTypography>
      </Box>
    </CustomTooltip>
  );
};

export default function getTicketLogContent(log) {
  const tool_info = IntegrationsMapping[log.ticketing_tool];
  if (log.log_type === ActivityLogType.TICKET_CREATED) {
    return (
      <CustomLogTypography>
        created a {tool_info.shortDisplayName || tool_info.displayName} ticket
        for this control <TicketLink log={log} />
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKET_LINKED) {
    return (
      <CustomLogTypography>
        linked this control to a{" "}
        {tool_info.shortDisplayName || tool_info.displayName} ticket{" "}
        <TicketLink log={log} />
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKET_UNLINKED) {
    return (
      <CustomLogTypography>
        unlinked a {tool_info.shortDisplayName || tool_info.displayName} ticket
        from this control
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKET_DELETED) {
    return (
      <CustomLogTypography>
        deleted the {tool_info.shortDisplayName || tool_info.displayName} ticket{" "}
        <TicketLink log={log} /> linked to this control
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKETS_CREATED_FOR_ENTITIES) {
    return (
      <CustomLogTypography>
        created {tool_info.shortDisplayName || tool_info.displayName} tickets
        for {log.entities_count} misconfigurations
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKET_CREATED_FOR_ENTITY) {
    return (
      <CustomLogTypography>
        create the {tool_info.shortDisplayName || tool_info.displayName} ticket{" "}
        <TicketLink log={log} /> and linked to misconfiguration{" "}
        <EntityLink log={log} entityId={log.entity_id}>
          {log.entity_name}
        </EntityLink>
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKET_LINKED_FOR_ENTITY) {
    return (
      <CustomLogTypography>
        linked the {tool_info.shortDisplayName || tool_info.displayName} ticket{" "}
        <TicketLink log={log} /> to misconfiguration{" "}
        <EntityLink log={log} entityId={log.entity_id}>
          {log.entity_name}
        </EntityLink>
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKET_UNLINKED_FOR_ENTITY) {
    return (
      <CustomLogTypography>
        unlinked the {tool_info.shortDisplayName || tool_info.displayName}{" "}
        ticket <TicketLink log={log} /> to misconfiguration{" "}
        <EntityLink log={log} entityId={log.entity_id}>
          {log.entity_name}
        </EntityLink>
      </CustomLogTypography>
    );
  }

  if (log.log_type === ActivityLogType.TICKET_DELETED_FOR_ENTITY) {
    return (
      <CustomLogTypography>
        deleted the {tool_info.shortDisplayName || tool_info.displayName} ticket{" "}
        <TicketLink log={log} /> linked to misconfiguration{" "}
        <EntityLink log={log} entityId={log.entity_id}>
          {log.entity_name}
        </EntityLink>
      </CustomLogTypography>
    );
  }

  return null;
}
